import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BarChart3, AlertTriangle, Users, FileText, Sparkles, Share2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtMoney } from "@/pages/app/Packages";

const monthStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

export default function DeliveryIntelligence() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [month, setMonth] = useState(monthStr(new Date()));
  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    setMonth(monthStr(new Date(y, m - 1 + delta, 1)));
  };
  const [yy, mm] = month.split("-").map(Number);
  const monthLabel = new Date(yy, mm - 1, 1).toLocaleString("en", { month: "long", year: "numeric" });

  return (
    <>
      <PageHeader eyebrow="Delivery" title="Delivery Intelligence"
        description="Variance, delays, capacity and client-ready monthly reports." testid="di-header" />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-4">
        <div className="flex items-center border border-border w-fit">
          <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={() => shiftMonth(-1)} data-testid="di-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="px-3 text-sm font-semibold w-40 text-center" data-testid="di-month-label">{monthLabel}</span>
          <Button variant="ghost" size="sm" className="rounded-none h-9" onClick={() => shiftMonth(1)} data-testid="di-next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <Tabs defaultValue="variance" className="w-full">
          <TabsList className="rounded-none flex-wrap h-auto justify-start">
            <TabsTrigger value="variance" className="rounded-none" data-testid="di-tab-variance"><BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Variance</TabsTrigger>
            <TabsTrigger value="delays" className="rounded-none" data-testid="di-tab-delays"><AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Delays</TabsTrigger>
            <TabsTrigger value="capacity" className="rounded-none" data-testid="di-tab-capacity"><Users className="w-3.5 h-3.5 mr-1.5" /> Capacity</TabsTrigger>
            <TabsTrigger value="report" className="rounded-none" data-testid="di-tab-report"><FileText className="w-3.5 h-3.5 mr-1.5" /> Client report</TabsTrigger>
          </TabsList>
          <TabsContent value="variance" className="pt-4"><VarianceTab wsId={wsId} month={month} /></TabsContent>
          <TabsContent value="delays" className="pt-4"><DelaysTab wsId={wsId} month={month} /></TabsContent>
          <TabsContent value="capacity" className="pt-4"><CapacityTab wsId={wsId} month={month} /></TabsContent>
          <TabsContent value="report" className="pt-4"><ReportTab wsId={wsId} month={month} /></TabsContent>
        </Tabs>
      </div>
    </>
  );
}

function VarianceTab({ wsId, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    let live = true;
    api.get(`/workspaces/${wsId}/delivery/variance?month=${month}`).then((r) => { if (live) setData(r.data); }).catch(() => { if (live) setData({ rows: [], totals: {} }); });
    return () => { live = false; };
  }, [wsId, month]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const t = data.totals || {};
  return (
    <div className="space-y-3" data-testid="di-variance">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label="Committed" value={t.committed} testid="di-v-committed" />
        <Stat label="Delivered" value={t.delivered} testid="di-v-delivered" />
        <Stat label="Delivered value" value={fmtMoney(t.price)} testid="di-v-value" />
        <Stat label="Est hours" value={`${t.est_hours}h`} testid="di-v-est" />
        <Stat label="Actual hours" value={`${t.actual_hours}h`} testid="di-v-actual" />
        <Stat label="True margin" value={t.true_margin_pct != null ? `${t.true_margin_pct}%` : "—"} tone={t.true_margin_pct < 0 ? "text-red-600" : "text-emerald-700"} testid="di-v-margin" />
      </div>
      {data.rows.length === 0 ? <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">No package activity this month.</div> : (
        <div className="border border-border divide-y divide-border overflow-x-auto">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold min-w-[900px]">
            <div className="col-span-3">Line / Brand</div><div className="col-span-1 text-right">Committed</div><div className="col-span-1 text-right">Done</div>
            <div className="col-span-1 text-right">%</div><div className="col-span-2 text-right">Est vs actual (h)</div>
            <div className="col-span-2 text-right">Value / actual cost</div><div className="col-span-1 text-right">Effort Δ</div><div className="col-span-1 text-right">QC</div>
          </div>
          {data.rows.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-xs min-w-[900px]" data-testid={`di-v-row-${i}`}>
              <div className="col-span-3"><div className="font-semibold">{r.line_name}</div><div className="text-muted-foreground">{r.brand_name} · {r.package_name}</div></div>
              <div className="col-span-1 text-right font-mono">{r.committed}</div>
              <div className="col-span-1 text-right font-mono">{r.delivered}</div>
              <div className={`col-span-1 text-right font-mono ${r.delivery_pct != null && r.delivery_pct < 100 ? "text-amber-600" : "text-emerald-700"}`}>{r.delivery_pct ?? "—"}%</div>
              <div className="col-span-2 text-right font-mono">{r.est_hours} / {r.actual_hours}</div>
              <div className="col-span-2 text-right font-mono">{fmtMoney(r.delivered_value)} / {fmtMoney(r.actual_cost)}</div>
              <div className={`col-span-1 text-right font-mono ${r.effort_variance_pct > 0 ? "text-red-600" : "text-emerald-700"}`}>{r.effort_variance_pct != null ? `${r.effort_variance_pct > 0 ? "+" : ""}${r.effort_variance_pct}%` : "—"}</div>
              <div className="col-span-1 text-right font-mono">{r.avg_qc ?? "—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DelaysTab({ wsId, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    let live = true;
    api.get(`/workspaces/${wsId}/delivery/delays?month=${month}`).then((r) => { if (live) setData(r.data); }).catch(() => { if (live) setData({ overdue: [], at_risk: [] }); });
    return () => { live = false; };
  }, [wsId, month]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const Section = ({ title, rows, tone, testid }) => (
    <div data-testid={testid}>
      <div className="label-eyebrow text-[10px] mb-1.5">{title} · {rows.length}</div>
      {rows.length === 0 ? <div className="text-xs text-muted-foreground border border-dashed border-border p-4 text-center">None 🎉</div> : (
        <div className="border border-border divide-y divide-border">
          {rows.map((r) => (
            <div key={r.id} className="px-3 py-2 flex items-center gap-3 text-sm">
              <span className={`w-2 h-2 rounded-full shrink-0 ${tone}`} />
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{r.name}</div><div className="text-xs text-muted-foreground">{r.brand_name}</div></div>
              <span className="text-xs font-mono">{r.planned_date}</span>
              <Badge variant="outline" className="rounded-none text-[10px]">{r.status}</Badge>
              {r.days_overdue > 0 && <span className="text-xs text-red-600 font-mono">{r.days_overdue}d late</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="di-delays">
      <Section title="Overdue" rows={data.overdue} tone="bg-red-500" testid="di-overdue" />
      <Section title="At risk (due ≤3 days)" rows={data.at_risk} tone="bg-amber-500" testid="di-at-risk" />
    </div>
  );
}

function CapacityTab({ wsId, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    let live = true;
    api.get(`/workspaces/${wsId}/delivery/capacity?month=${month}`).then((r) => { if (live) setData(r.data); }).catch(() => { if (live) setData({ rows: [], n_weeks: 0 }); });
    return () => { live = false; };
  }, [wsId, month]);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const heat = (h, cap) => {
    if (!cap) return h > 0 ? "bg-red-100 text-red-800" : "bg-muted/40";
    const u = h / cap;
    return u === 0 ? "bg-muted/40" : u < 0.6 ? "bg-emerald-100 text-emerald-800" : u < 0.9 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";
  };
  const rows = data.rows.filter((r) => r.total_scheduled > 0 || r.headcount > 0);
  return (
    <div className="overflow-x-auto" data-testid="di-capacity">
      {rows.length === 0 ? <div className="text-sm text-muted-foreground border border-dashed border-border p-6 text-center">No scheduled effort this month.</div> : (
        <div className="border border-border divide-y divide-border min-w-[760px]">
          <div className="grid gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold" style={{ gridTemplateColumns: `200px 70px repeat(${data.n_weeks}, 1fr) 90px` }}>
            <div>Designation</div><div className="text-right">Head</div>
            {Array.from({ length: data.n_weeks }, (_, i) => <div key={i} className="text-center">W{i + 1}</div>)}
            <div className="text-right">Util %</div>
          </div>
          {rows.map((r) => (
            <div key={r.designation} className="grid gap-2 px-3 py-2 items-center text-xs" style={{ gridTemplateColumns: `200px 70px repeat(${data.n_weeks}, 1fr) 90px` }} data-testid={`di-cap-${r.designation}`}>
              <div className="font-semibold truncate">{r.designation}</div>
              <div className="text-right font-mono">{r.headcount}</div>
              {r.weeks.map((h, i) => <div key={i} className={`text-center font-mono py-1 ${heat(h, r.capacity_per_week)}`}>{h}h</div>)}
              <div className={`text-right font-mono ${r.utilization_pct > 90 ? "text-red-600" : ""}`}>{r.utilization_pct ?? "—"}</div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[10px] text-muted-foreground mt-2">Scheduled skill-hours from the content calendar vs capacity (headcount × 40h/week). Red = designation with demand but no employees or &gt;90% load.</p>
    </div>
  );
}

function ReportTab({ wsId, month }) {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (wsId) api.get(`/workspaces/${wsId}/brands`).then((r) => { setBrands(r.data || []); if (r.data?.[0]) setBrandId((b) => b || r.data[0].id); }).catch(() => {});
  }, [wsId]);
  useEffect(() => {
    if (!wsId || !brandId) return;
    let live = true;
    api.get(`/workspaces/${wsId}/delivery/report?brand_id=${brandId}&month=${month}`).then((r) => { if (live) setReport(r.data); }).catch(() => { if (live) setReport(null); });
    return () => { live = false; };
  }, [wsId, brandId, month]);

  const genDigest = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/delivery/report/ai-digest`, { brand_id: brandId, month });
      setReport((s) => ({ ...s, ai_digest: r.data.ai_digest }));
      toast.success("AI digest generated");
    } catch (e) { toast.error(apiErrorMessage(e, "Digest failed")); }
    finally { setBusy(false); }
  };
  const share = async () => {
    try {
      const r = await api.post(`/workspaces/${wsId}/delivery/report/share`, { brand_id: brandId, month });
      const url = `${window.location.origin}${r.data.url_path}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      setReport((s) => ({ ...s, share_token: r.data.share_token }));
      toast.success(`Share link copied: ${url}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Share failed")); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="di-report">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={brandId} onValueChange={setBrandId}>
          <SelectTrigger className="h-9 w-56 rounded-none" data-testid="di-report-brand"><SelectValue placeholder="Pick brand" /></SelectTrigger>
          <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" className="rounded-none border-violet-500/50 text-violet-600 ml-auto" onClick={genDigest} disabled={busy || !brandId} data-testid="di-report-ai">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" />{busy ? "Writing… (~20s)" : "AI digest"}</Button>
        <Button className="rounded-none" onClick={share} disabled={!brandId} data-testid="di-report-share"><Share2 className="w-3.5 h-3.5 mr-1.5" /> Share with client</Button>
      </div>
      {report && <ReportBody report={report} />}
      {report?.share_token && <p className="text-xs text-muted-foreground" data-testid="di-share-url">Client link: {window.location.origin}/report/{report.share_token}</p>}
    </div>
  );
}

export function ReportBody({ report }) {
  return (
    <div className="border border-border" data-testid="report-body">
      <div className="px-5 py-4 border-b border-border bg-muted/30 flex items-center gap-3">
        {report.brand_logo && <img src={report.brand_logo} alt="" className="w-10 h-10 object-contain bg-white border border-border" />}
        <div>
          <div className="text-lg font-bold">{report.brand_name}</div>
          <div className="text-xs text-muted-foreground">Monthly delivery report · {report.month_label}</div>
        </div>
      </div>
      <div className="p-5 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Committed" value={report.committed} testid="rb-committed" />
          <Stat label="Delivered" value={report.delivered} testid="rb-delivered" />
          <Stat label="Delivery" value={report.delivery_pct != null ? `${report.delivery_pct}%` : "—"} testid="rb-pct" />
          <Stat label="Avg QC score" value={report.avg_qc != null ? `${report.avg_qc}/5` : "—"} testid="rb-qc" />
        </div>
        {report.warnings?.length > 0 && (
          <div className="border border-amber-500/40 bg-amber-50 p-3 space-y-1" data-testid="rb-warnings">
            {report.warnings.map((w, i) => <div key={i} className="text-xs text-amber-800">⚠ {w}</div>)}
          </div>
        )}
        {report.pillar_balance?.length > 0 && (
          <div data-testid="rb-pillars">
            <div className="label-eyebrow text-[10px] mb-1.5">Content pillar balance</div>
            <div className="space-y-1.5">
              {report.pillar_balance.map((p) => (
                <div key={p.pillar} className="flex items-center gap-2 text-xs">
                  <span className="w-32 truncate font-medium">{p.pillar}</span>
                  <div className="flex-1 h-3 bg-muted relative">
                    <div className="absolute inset-y-0 left-0 bg-violet-500/70" style={{ width: `${Math.min(100, p.actual_pct)}%` }} />
                    <div className="absolute inset-y-0 border-r-2 border-foreground/60" style={{ left: `${Math.min(100, p.target_pct)}%` }} />
                  </div>
                  <span className="font-mono w-24 text-right">{p.actual_pct}% / {p.target_pct}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div data-testid="rb-categories">
          <div className="label-eyebrow text-[10px] mb-1.5">By category</div>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(report.by_category || {}).map(([c, v]) => (
              <span key={c} className="text-xs border border-border px-2 py-1">{c}: <strong>{v.delivered}</strong>/{v.planned}</span>
            ))}
          </div>
        </div>
        {report.ai_digest && (
          <div className="border border-violet-500/30 bg-violet-50/50 p-4" data-testid="rb-digest">
            <div className="label-eyebrow text-[10px] mb-2 text-violet-700">Intelligence digest</div>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">{report.ai_digest}</div>
          </div>
        )}
        {report.published?.length > 0 && (
          <div data-testid="rb-published">
            <div className="label-eyebrow text-[10px] mb-1.5">Published deliverables</div>
            <div className="border border-border divide-y divide-border">
              {report.published.map((p, i) => (
                <div key={i} className="px-3 py-1.5 text-xs flex gap-3"><span className="font-mono text-muted-foreground">{p.date}</span><span className="font-medium flex-1">{p.name}</span>{p.pillar && <Badge variant="outline" className="rounded-none text-[9px]">{p.pillar}</Badge>}</div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone = "", testid }) {
  return (
    <div className="border border-border p-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold font-mono ${tone}`}>{value ?? "—"}</div>
    </div>
  );
}
